import { CheckCircle2, Lock, Unlock, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Textarea } from '../components/ui/Textarea';
import { usePermission } from '../auth/usePermission';
import { Permission } from '../auth/permissions';
import i18n from '../i18n';
import { formatEUR, formatDate } from '../lib/format';
import { PageHeader } from '../components/ui/PageHeader';

interface Session {
  id: string;
  user_id: string;
  status: string;
  opening_balance: string | number;
  closing_balance?: string | number;
  expected_balance?: string | number;
  variance?: string | number;
  total_cash?: string | number;
  total_sales_ttc?: string | number;
  ticket_count?: string | number;
  opened_at: string;
  closed_at?: string;
  validated_at?: string;
  validation_note?: string | null;
}

const STATUS_CLASS: Record<string, string> = {
  OPEN: 'bg-blue-100 text-blue-800',
  CLOSED: 'bg-warning/20 text-warning',
  VALIDATED: 'bg-success/15 text-success',
};
function StatusPill({ s }: { s: string }) {
  return (
    <span
      className={`text-xs px-2 py-0.5 rounded ${STATUS_CLASS[s] ?? 'bg-muted text-foreground'}`}
    >
      {i18n.t(`pos.status.${s}`, { defaultValue: s })}
    </span>
  );
}

const MIN_NOTE = 20;

/** Screen 45 — POS register: open, close, validate (variance + threshold-gated note). */
export default function PosClose() {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const canOpen = usePermission(Permission.POS_SESSION_OPEN);
  const canClose = usePermission(Permission.POS_SESSION_CLOSE);
  const canValidate = usePermission(Permission.POS_SESSION_VALIDATE);

  const [sessions, setSessions] = useState<Session[]>([]);
  const [opening, setOpening] = useState('');
  const [actual, setActual] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  // Variance tolerance above which validation requires a justification note (admin setting).
  const [threshold, setThreshold] = useState(10);
  // Session currently being validated (drives the note modal).
  const [validating, setValidating] = useState<Session | null>(null);
  const [note, setNote] = useState('');

  const reload = async () => {
    try {
      const { data } = await apiClient.get<Session[]>('/pos/sessions');
      setSessions(data ?? []);
    } catch (err) {
      showApiError(err);
    }
  };
  useEffect(() => {
    void reload();
    // The variance threshold is an admin setting readable by MGR/OWN (who can validate).
    if (canValidate) {
      apiClient
        .get<{ key: string; value: string }[]>('/admin/settings')
        .then((r) => {
          const row = r.data?.find((s) => s.key === 'posVarianceAlertThreshold');
          if (row && row.value !== '') setThreshold(Number(row.value));
        })
        .catch(() => {
          /* keep default 10 — purely advisory; the backend re-enforces on validate */
        });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const open = async () => {
    setBusy(true);
    try {
      await apiClient.post(
        '/pos/open',
        { openingBalance: Number(opening) },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('pos.sessionOpened') });
      setOpening('');
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  const close = async (id: string) => {
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ variance: number; expected: number }>(
        `/pos/sessions/${id}/close`,
        { actualBalance: Number(actual[id] ?? 0) },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      const v = Number(data.variance);
      push({
        tone: Math.abs(v) > threshold ? 'warning' : 'success',
        message: t('pos.closed', {
          variance: formatEUR(v),
          expected: formatEUR(data.expected),
        }),
      });
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  const confirmValidate = async () => {
    if (!validating) return;
    setBusy(true);
    try {
      await apiClient.post(
        `/pos/sessions/${validating.id}/validate`,
        { note: note.trim() || undefined },
        { headers: { 'X-Command-Id': crypto.randomUUID() } }
      );
      push({ tone: 'success', message: t('pos.sessionValidated') });
      setValidating(null);
      setNote('');
      await reload();
    } catch (err) {
      showApiError(err, { onReload: reload });
    } finally {
      setBusy(false);
    }
  };

  const varianceClass = (v: number) =>
    Math.abs(v) > threshold
      ? 'text-destructive font-medium'
      : v !== 0
        ? 'text-warning'
        : 'text-muted-foreground';

  // Modal enforcement mirrors the backend: a note ≥20 chars is mandatory only when the
  // absolute variance exceeds the threshold; otherwise it's optional.
  const validatingVariance = validating ? Number(validating.variance ?? 0) : 0;
  const noteRequired = Math.abs(validatingVariance) > threshold;
  const noteOk = !noteRequired || note.trim().length >= MIN_NOTE;

  return (
    <div className="space-y-4">
      <PageHeader title={t('pos.title')} />

      {canOpen && (
        <section className="border rounded p-4 bg-card">
          <h2 className="font-semibold mb-2">{t('pos.openSession')}</h2>
          <div className="flex gap-2 items-center text-sm">
            <Input
              type="number"
              min={0}
              step={0.01}
              className="w-44"
              value={opening}
              onChange={(e) => setOpening(e.target.value)}
              placeholder={t('pos.openingFloat')}
            />
            <Button size="sm" disabled={busy || opening === ''} onClick={open}>
              <Unlock className="h-4 w-4" />
              {t('pos.open')}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">{t('pos.oneOpenOnly')}</p>
        </section>
      )}

      <section className="border rounded p-4 bg-card">
        <h2 className="font-semibold mb-2">{t('pos.sessions')}</h2>
        <table className="w-full text-sm">
          <thead className="text-muted-foreground">
            <tr>
              <th className="text-left py-1">{t('pos.opened')}</th>
              <th>{t('common.status')}</th>
              <th className="text-right">{t('pos.float')}</th>
              <th className="text-right">{t('pos.salesTtc')}</th>
              <th className="text-right">{t('pos.expected')}</th>
              <th className="text-right">{t('pos.variance')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((s) => {
              const v = s.variance != null ? Number(s.variance) : null;
              return (
                <tr key={s.id} className="border-t align-top">
                  <td className="py-1">{formatDate(s.opened_at)}</td>
                  <td>
                    <StatusPill s={s.status} />
                    {s.status === 'VALIDATED' && s.validation_note && (
                      <div
                        className="text-xs text-muted-foreground max-w-[14rem] truncate"
                        title={s.validation_note}
                      >
                        « {s.validation_note} »
                      </div>
                    )}
                  </td>
                  <td className="text-right">{formatEUR(s.opening_balance)}</td>
                  <td className="text-right">
                    {s.total_sales_ttc != null ? formatEUR(s.total_sales_ttc) : '—'}
                  </td>
                  <td className="text-right">
                    {s.expected_balance != null ? formatEUR(s.expected_balance) : '—'}
                  </td>
                  <td className={`text-right ${v != null ? varianceClass(v) : ''}`}>
                    {v != null ? formatEUR(v) : '—'}
                    {v != null && v !== 0 && (
                      <span className="text-xs ml-1">
                        {v > 0 ? t('pos.shortage') : t('pos.surplus')}
                      </span>
                    )}
                  </td>
                  <td className="text-right">
                    {s.status === 'OPEN' && canClose && (
                      <span className="inline-flex gap-1 items-center">
                        <Input
                          type="number"
                          min={0}
                          step={0.01}
                          className="h-8 w-28 text-xs"
                          value={actual[s.id] ?? ''}
                          onChange={(e) => setActual({ ...actual, [s.id]: e.target.value })}
                          placeholder={t('pos.countedEuro')}
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy || (actual[s.id] ?? '') === ''}
                          onClick={() => close(s.id)}
                        >
                          <Lock className="h-4 w-4" />
                          {t('pos.close')}
                        </Button>
                      </span>
                    )}
                    {s.status === 'CLOSED' && canValidate && (
                      <Button
                        variant="success"
                        size="sm"
                        disabled={busy}
                        onClick={() => {
                          setValidating(s);
                          setNote('');
                        }}
                      >
                        <CheckCircle2 className="h-4 w-4" />
                        {t('pos.validate')}
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
            {sessions.length === 0 && (
              <tr>
                <td colSpan={7} className="py-4 text-center text-muted-foreground">
                  {t('pos.noSessions')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </section>

      {validating && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card border rounded-lg shadow-lg w-full max-w-md p-5 space-y-3">
            <h2 className="font-semibold text-lg">{t('pos.validateTitle')}</h2>
            <div className="text-sm flex justify-between">
              <span className="text-muted-foreground">{t('pos.observedVariance')}</span>
              <span className={varianceClass(validatingVariance)}>
                {formatEUR(validatingVariance)}
                {validatingVariance !== 0 && (
                  <span className="text-xs ml-1">
                    {validatingVariance > 0 ? t('pos.shortage') : t('pos.surplus')}
                  </span>
                )}
              </span>
            </div>
            <div className="text-sm flex justify-between">
              <span className="text-muted-foreground">{t('pos.tolerance')}</span>
              <span>{formatEUR(threshold)}</span>
            </div>
            {noteRequired ? (
              <div className="text-xs rounded border border-warning/40 bg-warning/10 text-warning px-3 py-2">
                {t('pos.noteRequired', { min: MIN_NOTE })}
              </div>
            ) : (
              <div className="text-xs text-muted-foreground">{t('pos.noteOptional')}</div>
            )}
            <div>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={3}
                placeholder={t('pos.notePlaceholder')}
              />
              {noteRequired && (
                <div
                  className={`text-xs mt-0.5 ${note.trim().length >= MIN_NOTE ? 'text-success' : 'text-muted-foreground'}`}
                >
                  {t('pos.noteCount', { count: note.trim().length, min: MIN_NOTE })}
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button
                variant="outline"
                size="sm"
                disabled={busy}
                onClick={() => {
                  setValidating(null);
                  setNote('');
                }}
              >
                <X className="h-4 w-4" />
                {t('common.cancel')}
              </Button>
              <Button
                variant="success"
                size="sm"
                disabled={busy || !noteOk}
                onClick={confirmValidate}
              >
                <CheckCircle2 className="h-4 w-4" />
                {t('pos.validate')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
